/**
 * `/raporty/$templateId` and `/raporty/nowa` — KANON `04-edytor.html`.
 *
 * One screen, two entries. A stored form hydrates from its own read; `/raporty/nowa` starts a draft
 * and its first save POSTs it. **Either way a save that lands returns to `/raporty`** — owner,
 * 17 September 2026 — so neither entry stays on the editor afterwards; `useSurveyEditor`'s header
 * carries the two exceptions.
 *
 * ── ONE column, centred ──────────────────────────────────────────────────────────────────────
 * The client phone preview that stood on the right was removed on 17 September 2026 (owner). What
 * is left is the form at its own measure, centred in the page — `.columns` already centred its
 * children, so the column found the middle of the screen the moment the second one went.
 *
 * ── The page is BOUNDED, and that is what stopped the jump ───────────────────────────────────
 * `<main>` wears `shared/ui/canonScreen`'s `.page`, the same 100dvh-and-hidden box every other
 * screen in this app stands in. It used to declare only `display: flex`, so the main had no height
 * bound at all: `.columns`'s own `overflow-y: auto` never engaged, the DOCUMENT scrolled instead,
 * and the form landing made the page 276px taller in one frame — measured. With the bound in place
 * the growth happens inside the scroller that was always meant to take it.
 *
 * ── The screen ENTERS, once, as one object ───────────────────────────────────────────────────
 * The same `rise` the hub and `/plans` take. It sits on the INNER box rather than on `<main>`,
 * because the rise translates 14px and a transform on a 100dvh element overflows the document for
 * the length of the step — a scrollbar appearing and going again, which is the very movement this
 * screen was corrected for. Inside the bounded page it is clipped.
 */

import { useId, useRef } from 'react';
import { Link } from '@tanstack/react-router';
import { Menu } from '@base-ui/react/menu';
import { motion } from 'motion/react';
import { useTranslation } from 'react-i18next';

import { screenMotion, useScreenMotionPreference } from '~/shared/design-system/motion';
import { useAnnouncer } from '~/shared/lib/useAnnouncer';
import { useDocumentTitle } from '~/shared/lib/useDocumentTitle';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import { EmptyState } from '~/shared/ui/EmptyState';
import { NoteMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { LiveRegionAnnouncer } from '~/shared/ui/LiveRegionAnnouncer';
import menuStyles from '~/shared/ui/MenuSurface.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import { SaveGlyph } from '~/shared/ui/SaveGlyph';
import saveButton from '~/shared/ui/saveButton.module.css';
import shell from '~/shared/ui/canonScreen.module.css';
import skel from '~/shared/ui/skeleton.module.css';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import {
  EditorForm,
  saveRefusalMessage,
  useEditorFormWiring,
} from '~/features/survey-question-editor';
import { useSurveyEditor, type EditorTarget } from '../lib/useSurveyEditor';
import { BackIcon, MoreIcon } from '~/shared/ui/ReportIcons';
import { PublicFormIcon } from '~/shared/ui/PublicFormIcons';
import canon from '~/shared/ui/surveyCanon.module.css';
import styles from './SurveyEditorScreen.module.css';

export interface SurveyEditorScreenProps {
  readonly target: EditorTarget;
}

export function SurveyEditorScreen({ target }: SurveyEditorScreenProps) {
  const { t } = useTranslation();
  const reducedMotion = useScreenMotionPreference();
  const editor = useSurveyEditor(target);
  const announcer = useAnnouncer();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const saveReasonId = useId();
  const duplicateReasonId = useId();
  const wiring = useEditorFormWiring({
    draft: editor.draft,
    dispatch: editor.dispatch,
    announce: announcer.announce,
    t,
  });

  const title =
    editor.draft.title === ''
      ? t('web:reports.editor.untitled')
      : editor.draft.title;
  useDocumentTitle(title);

  return (
    <main className={shell.page}>
      <motion.div className={styles.body} {...screenMotion('rise', { reducedMotion })}>
        <div className={styles.bar}>
          <Link
            to="/raporty"
            className={`${String(canon.ibtn)} ${String(canon.ibtnMd)}`}
            aria-label={t('web:reports.editor.back')}
          >
            <BackIcon className={canon.ibtnIconMd} />
          </Link>

          <span className={styles.crumbs}>
            <Link to="/raporty">{t('web:reports.title')}</Link>
            <span aria-hidden>/</span>
            {/* `AppShell` moves focus to the screen's heading after a route change, so it renders in
              * every state rather than waiting for a read. */}
            <h1 ref={headingRef} className={styles.crumbCurrent}>
              {editor.state === 'ready' ? (
                title
              ) : (
                <>
                  {/* Named for a listener, reserved for a reader. The bar is the same height either
                    * way, so nothing below it moves when the name lands. */}
                  <span className={hidden.srOnly}>{t('web:reports.editor.loading')}</span>
                  <i
                    className={
                      editor.skeleton
                        ? `${String(skel.bar)} ${String(styles.crumbSkel)}`
                        : styles.crumbSkel
                    }
                    aria-hidden
                  />
                </>
              )}
            </h1>
          </span>

          <span className={styles.barSpacer} />

          <span className={styles.barSlot}>
            {/* The cadence chip that stood here was removed on 17 September 2026 (owner: „bez
              * sensu" — the form states its own rhythm two inches below). What rode on it and is
              * NOT a restatement is whether the form is paused: nothing else on this screen says
              * so, and „Wznów ankietę" inside a closed menu is not a signal. It is a neutral chip,
              * because a paused form is a fact rather than an act. */}
            {editor.state === 'ready' && !editor.draft.active ? (
              <span className={canon.chip}>{t('web:reports.editor.pausedChip')}</span>
            ) : null}

            {/* A form that does not exist yet cannot be copied, paused or deleted — and neither can
              * one still in its wait. The draft is hydrated the moment the reads answer, which is
              * up to 300ms before the gate retires the bars, so the bar would grow a ⋯ over a
              * skeleton form if this asked the draft alone. */}
            {/* The public form's template is styled and shared from its builder; the way there
              * stands where the ⋯ stands for a check-in (public-form R4). */}
            {editor.state === 'ready' && editor.draft.kind === 'leadForm' ? (
              <Link to="/strona" className={canon.chip} title={t('web:publicForm.builder.nav')}>
                <PublicFormIcon className={canon.chipIcon} />
                {t('web:publicForm.builder.reports.appearanceLink')}
              </Link>
            ) : null}

            {editor.state !== 'ready' ||
            editor.draft.templateId === null ||
            editor.draft.kind !== 'checkIn' ? null : (
              <Menu.Root>
                <Menu.Trigger
                  className={`${String(canon.ibtn)} ${String(canon.ibtnMd)}`}
                  aria-label={t('web:reports.editor.menu.trigger')}
                >
                  <MoreIcon className={canon.ibtnIconMd} />
                </Menu.Trigger>
                <Menu.Portal>
                  <Menu.Positioner className={menuStyles.positioner} sideOffset={6} align="end">
                    {/* The app's one menu surface, worn the way the roster's and the hub's own ⋯
                      * wear it: a word per entry and nothing else. The glyph tiles this menu used to
                      * draw were `menuStyles.mark`, which is the CHECKBOX of a checkable list, and
                      * the two grey sentences under the items are said where they belong — the
                      * duplicate's refusal on the item itself, the delete's consequence in the
                      * confirmation, which words it more fully than the footnote did. */}
                    <Menu.Popup className={menuStyles.popup}>
                      {/* A copy is taken from the SAVED form, so a dirty draft is refused here with
                        * the reason rather than silently carried into a new template.
                        * `closeOnClick={false}` keeps the menu open on a refused press. */}
                      <Menu.Item
                        className={menuStyles.item}
                        aria-disabled={editor.duplicateRefusal !== null ? true : undefined}
                        aria-describedby={
                          editor.duplicateRefusal === null ? undefined : duplicateReasonId
                        }
                        title={editor.duplicateRefusal ?? undefined}
                        closeOnClick={editor.duplicateRefusal === null}
                        onClick={editor.duplicate}
                      >
                        <span className={menuStyles.itemLabel}>
                          {t('web:reports.editor.menu.duplicate')}
                        </span>
                        {/* Rendered, never drawn: a `title` reaches neither a touch trainer nor a
                          * keyboard one who never hovers, and the popup draws words only. */}
                        {editor.duplicateRefusal === null ? null : (
                          <span id={duplicateReasonId} className={hidden.srOnly}>
                            {editor.duplicateRefusal}
                          </span>
                        )}
                      </Menu.Item>
                      <Menu.Item className={menuStyles.item} onClick={editor.togglePaused}>
                        <span className={menuStyles.itemLabel}>
                          {editor.draft.active
                            ? t('web:reports.editor.menu.pause')
                            : t('web:reports.editor.menu.resume')}
                        </span>
                      </Menu.Item>
                      <div className={menuStyles.separator} role="separator" />
                      <Menu.Item
                        className={`${String(menuStyles.item)} ${String(styles.menuDestructive)}`}
                        onClick={editor.requestDelete}
                      >
                        <span className={menuStyles.itemLabel}>
                          {t('web:reports.editor.menu.delete')}
                        </span>
                      </Menu.Item>
                    </Menu.Popup>
                  </Menu.Positioner>
                </Menu.Portal>
              </Menu.Root>
            )}
          </span>

          {/* The plan builder's save, the same square and the same glyph — owner, 17 September 2026.
            * It was a 40px ink button carrying the words „Zapisz zmiany"; a trainer who saves a plan
            * and a trainer who saves a form are the same trainer, and two drawings of one act is the
            * duplication the house rules exist to stop. `shared/ui/saveButton.module.css` is the
            * treatment (promoted here, on this second consumer) and `SaveGlyph` the one floppy.
            *
            * A control with no visible word carries its whole name in `aria-label` and in `title`,
            * and the `title` gives way to the REFUSAL when there is one — which is the builder's own
            * shape for the same button. */}
          <button
            type="button"
            className={saveButton.save}
            aria-label={t('web:reports.editor.save.label')}
            // `aria-disabled`, never `disabled`: the refusal is the whole reason the control is there
            // and a `disabled` button drops out of the tab order taking its explanation with it.
            aria-disabled={editor.refusal !== null || editor.saving ? true : undefined}
            aria-describedby={editor.refusal === null ? undefined : saveReasonId}
            title={
              editor.refusal === null
                ? t('web:reports.editor.save.label')
                : saveRefusalMessage(editor.refusal, t)
            }
            onClick={editor.save}
          >
            <SaveGlyph />
            {/* Rendered, never drawn: the bar has no room for a sentence, and a `title` reaches
              * neither a touch trainer nor a keyboard one who never hovers. */}
            {editor.refusal === null ? null : (
              <span id={saveReasonId} className={hidden.srOnly}>
                {saveRefusalMessage(editor.refusal, t)}
              </span>
            )}
          </button>
        </div>

        <div className={styles.columns}>
          {editor.state === 'error' ? (
            <div className={styles.state}>
              <EmptyState
                mark={<NoteMarkIcon />}
                tone="neutral"
                title={t('web:reports.editor.states.errorTitle')}
                hint={t('web:reports.editor.states.errorMessage')}
              />
              {/* The same promoted retry the other four surfaces of this page draw. The hook has
                * always exposed `onRetry`; nothing reached it, so a failed read was a dead end. */}
              <button
                type="button"
                className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                onClick={editor.onRetry}
              >
                {t('web:reports.surveys.states.retry')}
              </button>
            </div>
          ) : editor.state === 'pending' ? (
            <FormSkeleton skeleton={editor.skeleton} />
          ) : (
            /* The gate's own hand-over rather than a second `screenMotion` step: the content is
              * replacing a skeleton, and a 10px translate on top of that cross-fade is movement the
              * loading policy spends nothing else on. Measured at 10px before the change. */
            <div className={`${String(styles.form)} ${editor.reveal}`}>
              {editor.writeFailed ? (
                <p className={styles.banner} role="alert">
                  {t('web:reports.editor.save.failed')}
                </p>
              ) : null}
              {/* `?klient=`: the form exists once saved, so a refused assignment says THAT and
                * offers the one act left — the trainer is not sent to a screen that cannot say it. */}
              {editor.clientAssign === 'failed' ? (
                <p className={styles.banner} role="alert">
                  {t('web:reports.editor.assignFailed')}{' '}
                  <button
                    type="button"
                    className={inlineButton.inlineTextButton}
                    onClick={editor.retryClientAssign}
                  >
                    {t('web:reports.editor.assignRetry')}
                  </button>
                </p>
              ) : editor.forClient !== null && editor.forClient.name !== null ? (
                <p className={styles.forClient}>
                  {editor.clientAssign === 'sending'
                    ? t('web:reports.editor.assigning')
                    : t('web:reports.editor.forClient', { client: editor.forClient.name })}
                </p>
              ) : null}
              <EditorForm
                draft={editor.draft}
                dispatch={editor.dispatch}
                assignment={{
                  assignedIds: editor.assignedIds,
                  faces: editor.faces,
                  onAssign: editor.assign,
                  onUnassign: editor.unassign,
                }}
                {...wiring}
                announce={announcer.announce}
                t={t}
              />
            </div>
          )}
        </div>
      </motion.div>

      <LiveRegionAnnouncer message={announcer.message} />

      <ConfirmDialog
        prompt={editor.confirm}
        onConfirm={editor.onConfirm}
        onCancel={editor.onCancelConfirm}
        onAlternate={editor.onAlternate}
      />
    </main>
  );
}

/**
 * The wait, inside the reserve the form column already holds.
 *
 * Five bands rather than a spinner, and they are the shape of the sections that land in them: the
 * name block, the assigned bar and three question sections. The gate decides whether the bars are
 * painted; the box is the same height either way.
 */
function FormSkeleton({ skeleton }: { readonly skeleton: boolean }) {
  return (
    <div className={styles.form} aria-hidden={!skeleton}>
      {[0, 1, 2, 3, 4].map((band) => (
        <div key={band} className={styles.skelSection}>
          {skeleton ? (
            <>
              <i className={`${String(skel.bar)} ${String(styles.skelRow)} ${String(styles.skelWide)}`} />
              <i className={`${String(skel.bar)} ${String(styles.skelRow)} ${String(styles.skelNarrow)}`} />
            </>
          ) : null}
        </div>
      ))}
    </div>
  );
}
